import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowLeft,
  FileText,
  Shield,
  DollarSign,
  Clock,
  AlertTriangle,
  Lock,
  RefreshCw,
  Phone,
  Mail,
  MapPin,
  CheckCircle,
  Users,
  Truck,
  CreditCard,
  UserCheck,
  Scale,
  Ban,
  CheckCircle2,
  ChevronRight
} from 'lucide-react';
import './TermsAndConditions.css';

const TermsAndConditions = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const sections = [
    {
      id: 1,
      title: 'Acceptance of Terms',
      icon: <CheckCircle className="section-icon" />,
      content:
        "By accessing or using Scrap Sector's website, mobile applications, or services, you agree to be bound by these Terms & Conditions, our Privacy Policy, Return Policy, and Refund Policy. If you do not agree with any part of these Terms, you must discontinue use of our services.",
      color: '#2F9621'
    },

    {
      id: 2,
      title: 'Services & Eligibility',
      icon: <Truck className="section-icon" />,
      content:
        'Scrap Sector provides technology-enabled recyclable scrap collection services through its Customer and Pickup Partner (Feriwala/Rider) applications. Pickup requests may be fulfilled on-demand or at scheduled times depending on service availability.',
      list: [
        'Doorstep scrap pickup services.',
        'On-demand and scheduled pickup requests.',
        'Fair valuation based on current market rates.',
        'Digital order management.',
        'Services are available only in supported service areas.',
        'Users must be at least 18 years of age to use the Platform.'
      ],
      color: '#2F9621'
    },

    {
      id: 3,
      title: 'Customer Responsibilities',
      icon: <Users className="section-icon" />,
      content:
        'Customers are responsible for ensuring that all information provided to Scrap Sector is accurate and complete.',
      list: [
        'Provide accurate pickup address and contact information.',
        'Upload authentic photos that accurately represent the scrap.',
        'Be present or authorize another person during pickup.',
        'Provide only legally owned scrap materials.',
        'Present proof of ownership where legally required.',
        'Do not offer stolen, hazardous, prohibited, or illegal materials.',
        'Follow all instructions provided by the assigned pickup partner.'
      ],
      color: '#2F9621'
    },

    {
      id: 4,
      title: 'Pickup Partner Responsibilities',
      icon: <Truck className="section-icon" />,
      content:
        'Pickup Partners (Feriwalas/Riders) are independent service providers responsible for complying with applicable laws while providing services through the Platform.',
      list: [
        'Provide accurate registration and verification information.',
        'Maintain valid licenses, registrations, and insurance where applicable.',
        'Accept and complete orders professionally.',
        'Handle customer scrap safely and honestly.',
        'Do not manipulate weights or pricing.',
        'Do not conduct off-platform transactions.',
        'Comply with all applicable traffic and safety regulations.'
      ],
      color: '#2F9621'
    },

    {
      id: 5,
      title: 'Payments & Cancellations',
      icon: <CreditCard className="section-icon" />,
      content:
        'Scrap values are determined based on current market rates, material quality, and weight. Platform fees, service charges, or cancellation charges may apply where applicable.',
      list: [
        'Prices may vary depending on market conditions.',
        'Payments are processed according to Scrap Sector policies.',
        'Customers may cancel bookings before pickup confirmation.',
        'Cancellation fees may apply after a pickup partner has been assigned.',
        'Applicable taxes and statutory charges remain the responsibility of the user.'
      ],
      color: '#2F9621'
    },

    {
      id: 6,
      title: 'Prohibited Activities',
      icon: <Shield className="section-icon" />,
      content:
        'The following activities are strictly prohibited while using Scrap Sector:',
      list: [
        'Selling stolen or illegal scrap materials.',
        'Providing false identity or misleading information.',
        'Abusing customers, pickup partners, or support staff.',
        'Attempting to bypass the Platform for direct transactions.',
        'Manipulating prices, weights, or payments.',
        'Violating applicable laws or regulations.'
      ],
      color: '#2F9621'
    },

    {
      id: 7,
      title: 'Limitation of Liability',
      icon: <Scale className="section-icon" />,
      content:
        'Scrap Sector acts as a technology platform connecting customers with independent pickup partners. To the maximum extent permitted by law, Scrap Sector shall not be liable for indirect, incidental, special, consequential, or punitive damages arising from the use of the Platform. Pickup partners remain responsible for their own actions while providing services.',
      color: '#2F9621'
    },

    {
      id: 8,
      title: 'Privacy & Data Protection',
      icon: <Lock className="section-icon" />,
      content:
        'Your privacy is important to us. Our collection, use, storage, and sharing of personal information is governed by our Privacy Policy. By using Scrap Sector, you consent to our data handling practices as described in the Privacy Policy.',
      color: '#2F9621'
    },

    {
      id: 9,
      title: 'Returns & Refunds',
      icon: <RefreshCw className="section-icon" />,
      content:
        'Once scrap has been collected and the transaction has been completed, the transaction is considered final. Scrap Sector generally follows a no-return and no-refund policy except where required by applicable law or where an operational error has occurred.',
      list: [
        'Completed pickups cannot be returned.',
        'Complaints may be submitted to Customer Support.',
        'Operational issues will be reviewed individually.',
        'Approved resolutions may include service correction or pickup rescheduling instead of monetary refunds.'
      ],
      color: '#2F9621'
    },

    {
      id: 10,
      title: 'Changes to These Terms',
      icon: <RefreshCw className="section-icon" />,
      content:
        "Scrap Sector may update these Terms & Conditions from time to time to reflect changes in our services, technology, or legal obligations. Updated Terms will be published on this page with a revised 'Last Updated' date. Continued use of the Platform after such changes constitutes acceptance of the updated Terms.",
      color: '#2F9621'
    }
  ];

  return (
    <div className="terms-page">

      {/* =====================================================
          BACKGROUND ELEMENTS
      ====================================================== */}

      <div className="background-elements">
        <div className="floating-shape shape-1"></div>
        <div className="floating-shape shape-2"></div>
        <div className="floating-shape shape-3"></div>
        <div className="floating-shape shape-4"></div>
      </div>


      <div className="terms-container">

        {/* =====================================================
            HERO
        ====================================================== */}

        <motion.div
          className="terms-hero"
          initial={{ opacity: 0, y: -50 }}
          animate={{
            opacity: isVisible ? 1 : 0,
            y: isVisible ? 0 : -50
          }}
          transition={{ duration: 0.8 }}
        >

          <div className="hero-content">

            <div className="hero-label">
              <Shield className="hero-label-icon" />
              <span>LEGAL &amp; TRANSPARENCY</span>
            </div>

            <div className="hero-icon">

              <FileText className="main-icon" />

              <div className="icon-glow"></div>

            </div>

            <h1 className="hero-title">
              Terms &amp;{' '}
              <span>Conditions</span>
            </h1>

            <p className="hero-subtitle">
              Your guide to using ScrapSector services safely,
              transparently, and responsibly.
            </p>

            <div className="hero-badge">
              <Clock className="badge-icon" />

              <span>
                Last updated: {new Date().toLocaleDateString()}
              </span>
            </div>

          </div>

        </motion.div>


        {/* =====================================================
            TRUST STRIP
        ====================================================== */}

        <motion.div
          className="terms-trust-strip"
          initial={{ opacity: 0, y: 25 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
        >

          <div className="trust-item">

            <div className="trust-icon">
              <Shield />
            </div>

            <div>
              <strong>Transparent Terms</strong>
              <span>Clear service guidelines</span>
            </div>

          </div>


          <div className="trust-divider"></div>


          <div className="trust-item">

            <div className="trust-icon">
              <Lock />
            </div>

            <div>
              <strong>Secure Platform</strong>
              <span>Your information matters</span>
            </div>

          </div>


          <div className="trust-divider"></div>


          <div className="trust-item">

            <div className="trust-icon">
              <UserCheck />
            </div>

            <div>
              <strong>Responsible Service</strong>
              <span>Fair &amp; professional process</span>
            </div>

          </div>

        </motion.div>


        {/* =====================================================
            CONTENT GRID
        ====================================================== */}

        <div className="terms-grid">

          {sections.map((section, index) => (

            <motion.div
              key={section.id}
              className="terms-card"
              initial={{
                opacity: 0,
                y: 50,
                scale: 0.95
              }}
              animate={{
                opacity: 1,
                y: 0,
                scale: 1
              }}
              transition={{
                duration: 0.6,
                delay: index * 0.08,
                type: 'spring',
                stiffness: 100
              }}
              whileHover={{
                y: -7,
                transition: {
                  duration: 0.2
                }
              }}
            >

              {/* Card Header */}

              <div
                className="card-header"
                style={{
                  background:
                    'linear-gradient(135deg, #102018 0%, #1F6418 100%)'
                }}
              >

                <div className="card-number">
                  {String(section.id).padStart(2, '0')}
                </div>

                <div className="card-header-title">
                  TERMS
                </div>

                <div
                  className="card-icon"
                  style={{
                    color: '#70BD7D'
                  }}
                >
                  {section.icon}
                </div>

              </div>


              {/* Card Body */}

              <div className="card-body">

                <div className="card-heading-row">

                  <h3 className="card-title">
                    {section.title}
                  </h3>

                  <ChevronRight className="card-arrow" />

                </div>


                <p className="card-text">
                  {section.content}
                </p>


                {/* List */}

                {section.list && (

                  <ul className="card-list">

                    {section.list.map((item, idx) => (

                      <li
                        key={idx}
                        className="list-item"
                      >

                        <div
                          className="list-icon"
                          style={{
                            background:
                              '#EAF6E7'
                          }}
                        >

                          <CheckCircle2
                            className="check-icon"
                          />

                        </div>

                        <span>
                          {item}
                        </span>

                      </li>

                    ))}

                  </ul>

                )}


                {/* Additional */}

                {section.additional && (

                  <div
                    className="card-highlight"
                    style={{
                      borderColor: '#2F9621'
                    }}
                  >

                    <AlertTriangle
                      className="highlight-icon"
                    />

                    <p>
                      {section.additional}
                    </p>

                  </div>

                )}

              </div>

            </motion.div>

          ))}

        </div>


        {/* =====================================================
            CONTACT SECTION
        ====================================================== */}

        <motion.section
          className="contact-section"
          initial={{
            opacity: 0,
            y: 50
          }}
          animate={{
            opacity: 1,
            y: 0
          }}
          transition={{
            duration: 0.8,
            delay: 0.5
          }}
        >

          <div className="contact-header">

            <div className="contact-label">
              <Phone />
              <span>NEED ASSISTANCE?</span>
            </div>

            <h3>
              Have Questions About Our Terms?
            </h3>

            <p>
              Our support team is available to help
              you understand our terms and services.
            </p>

          </div>


          <div className="contact-cards">

            {/* Email */}

            <motion.div
              className="contact-card"
              whileHover={{
                scale: 1.03,
                y: -3
              }}
              transition={{
                duration: 0.2
              }}
            >

              <div className="contact-icon-wrapper">
                <Mail className="contact-icon" />
              </div>

              <div className="contact-info">

                <h4>
                  Email Us
                </h4>

                <span>
                  scrapsectorbusiness@gmail.com
                </span>

              </div>

              <ChevronRight className="contact-arrow" />

            </motion.div>


            {/* Phone */}

            <motion.div
              className="contact-card"
              whileHover={{
                scale: 1.03,
                y: -3
              }}
              transition={{
                duration: 0.2
              }}
            >

              <div className="contact-icon-wrapper">
                <Phone className="contact-icon" />
              </div>

              <div className="contact-info">

                <h4>
                  Call Us
                </h4>

                <span>
                  7278000038
                </span>

              </div>

              <ChevronRight className="contact-arrow" />

            </motion.div>


            {/* Address */}

            <motion.div
              className="contact-card"
              whileHover={{
                scale: 1.03,
                y: -3
              }}
              transition={{
                duration: 0.2
              }}
            >

              <div className="contact-icon-wrapper">
                <MapPin className="contact-icon" />
              </div>

              <div className="contact-info">

                <h4>
                  Visit Us
                </h4>

                <span>
                  Manakpur, Thakurdass, Near Railway Crossing
                  (Peer Baba), Kalka, Pinjore, Panchkula,
                  Haryana, 134102
                </span>

              </div>

              <ChevronRight className="contact-arrow" />

            </motion.div>

          </div>

        </motion.section>


        {/* =====================================================
            FOOTER ACTION
        ====================================================== */}

        <motion.div
          className="terms-footer"
          initial={{
            opacity: 0,
            y: 30
          }}
          animate={{
            opacity: 1,
            y: 0
          }}
          transition={{
            duration: 0.6,
            delay: 1
          }}
        >

          <Link
            to="/"
            className="back-home-btn"
          >

            <ArrowLeft className="btn-icon" />

            <span>
              Back to Home
            </span>

            <div className="btn-glow"></div>

          </Link>

        </motion.div>

      </div>
    </div>
  );
};

export default TermsAndConditions;